import { bffMutation, bffQuery } from '../../lib/bffClient.js';
import { refreshAccessToken, refreshUntilRole } from '../../lib/auth.js';
import { linkFounderAsPractitioner } from '../../lib/practitionerLink.js';
import { fromDisplay } from '../../lib/currency';
import { matchCity, matchProvince, provinceOfCity } from '../../constants/iranGeo';
import { refOf } from '../../context/ClinicContext.jsx';
import { PERMISSION_CATALOG_QUERY } from '../People/constants.js';
import { DEFAULT_CLINIC_TYPE } from '../Clinic/Clinic.shared';
import { fetchOfferedDraw } from './DoctorTrack.jsx';
import {
  DEFAULT_VISIT_MINUTES, DEFAULT_VISIT_PRICE_TOMAN, defaultDoctorSetup, visitDeclarationInput,
} from './parsinaDefaults.js';

/**
 * Registration with nothing to fill in — for an account whose number is Iranian.
 *
 * «شروع» asks a doctor for ONE thing, the کد نظام پزشکی, and someone who is not
 * a doctor for one thing, the clinic's name. Everything the wizard used to ask
 * after that is decided here instead, and the person lands in their panel:
 *
 *   clinic   — named «مطب دکتر …» from the council's record (or what the
 *              non-doctor typed), in the province and city the council holds;
 *   defaults — every standard role, and «ویزیت» at the Parsina price
 *              (parsinaDefaults.js) — what «تنظیمات پیش‌فرض پارسینا» wrote;
 *   link     — doctor only: the founder joined to their own clinic, with the
 *              default week and visit length, and «ویزیت» switched on under them.
 *
 * Phone, address and the rest are left empty on purpose; «کلینیک من» lists them
 * as still to do. A number from abroad never comes here — it keeps the wizard.
 *
 * ── Why the run lives in this module and not in a component ─────────────────
 *
 * Creating the clinic changes the boot state from «nothing to enter» to
 * «ready», and App.jsx renders those two with different route trees — so the
 * screen showing this progress is UNMOUNTED and mounted again in the middle of
 * the run. The old wizard survived that because its next step waited for a
 * click. This one does not wait, so a run owned by the component would either
 * die with it or be started a second time by its replacement. Module state
 * survives the remount; the new component subscribes to the run in flight.
 *
 * ── Why it is also written to sessionStorage ────────────────────────────────
 *
 * A real reload kills the module too. The plan — and how far it got — is kept
 * in the tab, so starting again skips what is done: the clinic is never created
 * twice, and every later step is idempotent on the server (roles are seeded by
 * template key, «ویزیت» is an upsert, the join skips a link it already made).
 */

export const PLAN_KEY = 'onboarding.quick.v1';

export const TRACK = { DOCTOR: 'doctor', CLINIC: 'clinic' };
export const STAGE = { DOCTOR: 'doctor', CLINIC: 'clinic', DEFAULTS: 'defaults', LINK: 'link' };

const CREATE_CLINIC = `mutation CreateClinic($input: CreateClinicInput!) {
  createClinic(input: $input) { id name officePhone }
}`;

const SEED_ROLES = `mutation SeedClinicRoles($templateKeys: [String!]!) {
  seedClinicRoles(templateKeys: $templateKeys) { id nameFa roleKey }
}`;

const DECLARE_VISIT = `mutation SetClinicServiceDeclaration($input: ClinicServiceDeclarationInput!) {
  setClinicServiceDeclaration(input: $input) { id isActive }
}`;

const UPDATE_DOCTOR_LINK = `mutation UpdateDoctorLink($id: ID!, $input: UpdateDoctorLinkInput!) {
  updateDoctorLink(id: $id, input: $input) { id visitDurationMinutes }
}`;

// ── The plan ─────────────────────────────────────────────────────────────────

/** @returns {object|null} what «شروع» decided, plus how far the run got */
export function readQuickPlan() {
  try { return JSON.parse(sessionStorage.getItem(PLAN_KEY) || 'null'); } catch { return null; }
}

export function writeQuickPlan(plan) {
  try { sessionStorage.setItem(PLAN_KEY, JSON.stringify(plan)); } catch { /* the run still works; only a reload loses it */ }
}

export function clearQuickPlan() {
  try { sessionStorage.removeItem(PLAN_KEY); } catch { /* nothing depends on it going */ }
}

// i18n-exempt: the clinic's STORED name (invoices, patient messages), written once for an Iranian account — data, not UI copy
const OFFICE = 'مطب دکتر';
// The council sometimes prefixes the name with the title itself.
// i18n-exempt: a title stripped from registry data, never rendered
const TITLE = /^\s*(?:دکتر|دكتر|dr\.?)\s+/i;

/** «مطب دکتر مریم رضایی», from the council's «دکتر مریم رضایی» or «مریم رضایی». */
export function officeNameFor(fullName) {
  const name = String(fullName || '').replace(TITLE, '').trim();
  return name ? `${OFFICE} ${name}` : OFFICE;
}

/**
 * The council's province and city in OUR spelling, or empty — never a string
 * the «کلینیک من» pickers cannot show. A city alone still finds its province.
 */
export function placeFromRegistry(member) {
  const province = matchProvince(member?.province) || provinceOfCity(member?.city) || '';
  const city = (province && matchCity(province, member?.city)) || '';
  return { province, city };
}

/** «ویزیت» at the Parsina price. Iranian accounts only, so always IRR. */
const defaultVisit = () => ({
  priceMinor: fromDisplay(String(DEFAULT_VISIT_PRICE_TOMAN), 'IRR'),
  durationMinutes: DEFAULT_VISIT_MINUTES,
});

// ── The run ──────────────────────────────────────────────────────────────────

let run = null;
const listeners = new Set();

function emit(patch) {
  run = { ...run, ...patch };
  listeners.forEach((fn) => fn(run));
}

/**
 * The run in this tab, or null.
 * `{ track, stage, running, done, error, ref, clinicName, created, warnings, draw }`
 */
export const quickRun = () => run;

export function subscribeQuickRun(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Forget the run — after entering the clinic, or when abandoning it. */
export function resetQuickRun() {
  run = null;
  listeners.forEach((fn) => fn(run));
}

/**
 * Start the run, or join the one already going.
 *
 * @param {object} deps the ClinicContext pieces it needs — `memberships`,
 *   `capabilities`, `refreshGrants`, `reload`, `repairSetup`. They are the
 *   provider's, not the screen's, so they outlive the remount.
 * @param {{skipAccess?: boolean}} [opts] walk past a token refresh that failed
 */
export function startQuickSetup(deps, opts = {}) {
  if (run?.running) return run;
  const plan = readQuickPlan();
  run = {
    track: plan?.track || TRACK.CLINIC,
    stage: STAGE.CLINIC,
    running: true,
    done: false,
    error: null,
    ref: '',
    clinicName: plan?.clinicName || '',
    created: false,
    warnings: [],
    draw: null,
  };
  emit({});
  execute(deps, opts).catch((err) => {
    console.error(`[quickSetup] ${run?.stage} failed:`, err?.message || err);
    // `step` tells a clinic that could not be CREATED (nothing exists; retry
    // or go back) from a token that could not be REFRESHED (the clinic exists;
    // retry, or walk on without it as the old wizard allowed).
    emit({ running: false, error: { stage: run?.stage, step: run?.step, err } });
  });
  return run;
}

async function execute({ memberships = [], capabilities = {}, refreshGrants, reload, repairSetup }, { skipAccess = false }) {
  let plan = readQuickPlan();
  if (!plan?.track) throw new Error('no registration plan in this tab');
  const doctor = plan.track === TRACK.DOCTOR;
  const warnings = [...(plan.warnings || [])];
  const save = (next) => { plan = { ...plan, ...next }; writeQuickPlan(plan); };

  // ── 1 · The clinic ───────────────────────────────────────────────────────
  emit({ stage: STAGE.CLINIC, step: 'create' });
  if (!plan.clinicId) {
    const halfBuilt = memberships.find((m) => m.repairNeeded);
    const founder = memberships.find((m) => m.isFounder);
    if (halfBuilt) {
      // E1: a half-applied saga is FINISHED, never built again beside itself.
      await repairSetup(halfBuilt.clinicId);
      save({ clinicId: halfBuilt.clinicId, clinicName: halfBuilt.clinicName || plan.clinicName, existing: true });
    } else if (founder && !capabilities.multiFounder) {
      // One clinic per account here, and this account has it: a doctor who
      // founded it before becoming one is JOINED to it, not refused.
      save({ clinicId: founder.clinicId, clinicName: founder.clinicName || plan.clinicName, existing: true });
    } else {
      const input = {
        name: plan.clinicName,
        clinicType: DEFAULT_CLINIC_TYPE,
        province: plan.province || undefined,
        city: plan.city || undefined,
      };
      const created = await bffMutation(CREATE_CLINIC, { input }, 'CreateClinic');
      const id = created?.createClinic?.id;
      if (!id) throw new Error('createClinic answered without an id');
      save({ clinicId: id, clinicName: created.createClinic.name || plan.clinicName, created: true });
    }
  }
  emit({ clinicName: plan.clinicName, created: Boolean(plan.created) });

  // The token that can speak for the new clinic. Every call after this one is
  // clinic-scoped, so a stale token here is a 403 on all of them.
  emit({ step: 'access' });
  if (!skipAccess) await refreshAccessToken();
  const list = await refreshGrants();
  const target = (list || []).find((m) => m.clinicId === plan.clinicId);
  const ref = refOf(target) || plan.clinicId;

  // ── 2 · Parsina's defaults ───────────────────────────────────────────────
  //
  // Only on a clinic THIS run created. An existing clinic has a price for
  // «ویزیت» and the roles its owner chose — re-declaring the defaults over them
  // would undo real decisions.
  emit({ stage: STAGE.DEFAULTS, step: 'defaults' });
  if (!plan.existing && !plan.defaultsDone) {
    try {
      const catalog = await bffQuery(PERMISSION_CATALOG_QUERY, {}, 'PermissionCatalog');
      const keys = (catalog?.roleTemplates || []).map((x) => x.key);
      if (keys.length) await bffMutation(SEED_ROLES, { templateKeys: keys }, 'SeedClinicRoles');
    } catch (err) {
      console.error('[quickSetup] seeding roles failed:', err?.message || err);
      warnings.push('roles');
    }
    // Inactive: accounting refuses an active service nobody performs. The
    // join below is what gives it a performer and switches it on.
    let visitDeclared = false;
    try {
      await bffMutation(DECLARE_VISIT, { input: visitDeclarationInput(defaultVisit()) }, 'SetClinicServiceDeclaration');
      visitDeclared = true;
    } catch (err) {
      console.error('[quickSetup] declaring the visit service failed:', err?.message || err);
      warnings.push('visit');
    }
    save({ defaultsDone: true, visitDeclared, warnings });
  }

  // ── 3 · The founder joined to it (doctor only) ───────────────────────────
  if (doctor && !plan.linkDone) {
    emit({ stage: STAGE.LINK, step: 'link' });
    const link = await linkFounderAsPractitioner();   // never throws
    if (!link) warnings.push('link');
    // Only on the membership of THIS clinic: the join answers with the
    // founder's first owned clinic, and a new clinic's default week written
    // over an older clinic's real one is worse than writing nothing.
    const ownLink = Boolean(link?.id) && link.clinicId === plan.clinicId;
    if (ownLink && !plan.existing) {
      try {
        await bffMutation(UPDATE_DOCTOR_LINK, { id: link.id, input: defaultDoctorSetup() }, 'UpdateDoctorLink');
      } catch (err) {
        console.error('[quickSetup] updateDoctorLink failed:', err?.message || err);
        warnings.push('hours');
      }
      if (plan.visitDeclared && link.doctorId) {
        try {
          await bffMutation(
            DECLARE_VISIT,
            { input: visitDeclarationInput({ ...defaultVisit(), doctorId: link.doctorId }) },
            'SetClinicServiceDeclaration',
          );
        } catch (err) {
          console.error('[quickSetup] activating the visit service failed:', err?.message || err);
          warnings.push('visit');
        }
      }
    }
    // What the join granted, adopted before entering: without it the desk
    // offers «ثبت پرداخت» alone on the founder's own row until a manual reload.
    if (link) {
      await refreshUntilRole('doctor');
      try { await reload({ silent: true }); } catch { /* the next boot re-reads it */ }
    }
    save({ linkDone: true, warnings });
  }

  // A wheel a campaign owes this doctor, asked now that the record it is
  // decided from exists. Never throws; almost always null.
  const draw = doctor ? await fetchOfferedDraw() : null;

  clearQuickPlan();
  emit({ running: false, done: true, ref, warnings: [...new Set(warnings)], draw });
}
